import { useState } from "react";
import BrandLogo from "../../components/BrandLogo";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";
import { useNavigate } from "react-router-dom";
import SessionStorageHelper from "../../utils/sessionStorageHelper";
import { Button } from "../../components/ui/button";
import { gluceraAssessmentCta } from "../../utils/gluceraTypography";
import { Label } from "../../components/ui/label";
import { cn } from "../../lib/utils";

const HeightWeightPage = () => {
  const [unit, setUnit] = useState("Ft - Inch");
  const navigate = useNavigate();
  const [feet, setFeet] = useState<string | number>("");
  const [inches, setInches] = useState<string | number>("");
  const [cms, setCms] = useState<string | number>("");
  const [weight, setWeight] = useState<string | number>("");
  const [weightUnit, setWeightUnit] = useState("Lbs");

  const isHeightValid = () => {
    if (unit === "Ft - Inch") {
      const feetNum = Number(feet);
      const inchesNum = Number(inches);
      return (
        feet !== "" &&
        inches !== "" &&
        feetNum > 0 &&
        feetNum <= 10 &&
        inchesNum >= 0 &&
        inchesNum < 12
      );
    } else {
      const cmsNum = Number(cms);
      return cms !== "" && cmsNum > 0;
    }
  };

  const isWeightValid = () => {
    const weightNum = Number(weight);
    if (weightUnit === "Lbs") {
      return weight !== "" && weightNum >= 3 && weightNum <= 1000;
    } else {
      return weight !== "" && weightNum >= 3 && weightNum <= 500;
    }
  };

  const canProceed = isHeightValid() && isWeightValid();

  const handleSave = () => {
    if (canProceed) {
      const saveData: any = {};

      // Clear all height fields first, then set only selected one
      saveData.height_ft = null;
      saveData.height_inches = null;
      saveData.height_cm = null;

      if (unit === "Ft - Inch") {
        const feetNum = Number(feet);
        const inchesNum = Number(inches);
        saveData.height_ft = feetNum;
        saveData.height_inches = inchesNum;
      } else {
        saveData.height_cm = Number(cms);
      }

      // Clear all weight fields first, then set only selected one
      saveData.weight_kg = null;
      saveData.weight_lbs = null;

      if (weightUnit === "Kg") {
        saveData.weight_kg = Number(weight);
      } else {
        saveData.weight_lbs = Number(weight);
      }

      SessionStorageHelper.saveMultipleFields(saveData);

      navigate("/glucera" + GluceraNavigationRoutes.TYPE_OF_DIABETES);
    }
  };

  return (
    <div className="min-h-[100dvh] w-screen overflow-y-auto overscroll-contain bg-background">
      <div className="mx-auto flex min-h-[100dvh] w-full max-w-md flex-col">
        {/* Header with Progress */}
        <div className="space-y-3 px-4 pb-4 pt-safe">
          {/* <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="w-10 h-10 rounded-full bg-muted flex items-center justify-center hover:bg-muted/80 transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex-1">
              <Progress value={(4 / 12) * 100} className="h-2" />
              <p className="mt-2 text-sm text-muted-foreground">Step 4 of 12</p>
            </div>
          </div> */}
          <BrandLogo />

          {/* Assessment Progress */}
          <p className="text-center text-sm text-muted-foreground">
            Assessment 4 of 11
          </p>
        </div>

        {/* Content Area — page scrolls as one document so the Weight field stays reachable when the keyboard is open */}
        <div className="flex flex-1 flex-col px-4 pb-6 pt-2">
          {/* Title */}
          <div className="mb-3 sm:mb-4">
            <h1 className="mb-2 text-xl font-bold sm:text-2xl">
              What Is Your Height And Weight?
            </h1>
            <p className="text-xs text-muted-foreground sm:text-sm">
              It can be either in Cms or ft inches
            </p>
          </div>

          {/* Height Section */}
          <div className="mb-6">
            <Label className="mb-3 block text-base font-semibold text-foreground">
              Height
            </Label>

            {/* Height Unit Tabs */}
            <div className="mb-4 flex gap-2">
              <button
                className={cn(
                  "flex-1 rounded-lg px-4 py-2.5 text-sm font-semibold transition-all",
                  unit === "Ft - Inch"
                    ? "bg-[#737DFE] text-white shadow-sm"
                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                )}
                onClick={() => {
                  setUnit("Ft - Inch");
                  setCms("");
                }}
              >
                Ft - Inch
              </button>
              <button
                className={cn(
                  "flex-1 rounded-lg px-4 py-2.5 text-sm font-semibold transition-all",
                  unit === "CMS"
                    ? "bg-[#737DFE] text-white shadow-sm"
                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                )}
                onClick={() => {
                  setUnit("CMS");
                  setFeet("");
                  setInches("");
                }}
              >
                Cms
              </button>
            </div>

            {/* Height Inputs */}
            {unit === "Ft - Inch" ? (
              <div className="flex gap-3">
                <div className="flex flex-1 items-center rounded-lg border border-input bg-background focus-within:border-black">
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    min="1"
                    max="10"
                    value={feet}
                    onChange={e => {
                      const value = e.target.value;
                      if (
                        value === "" ||
                        (/^\d+$/.test(value) && Number(value) >= 0 && Number(value) <= 10)
                      ) {
                        setFeet(value);
                      }
                    }}
                    onKeyDown={e => {
                      if (e.key === "-" || e.key === "." || e.key === "e" || e.key === "E") {
                        e.preventDefault();
                      }
                    }}
                    className="w-full rounded-lg border-none bg-transparent py-3 pl-4 pr-1 text-foreground placeholder:text-muted-foreground focus:outline-none"
                    placeholder="0"
                  />
                  <span className="pr-3 text-base font-bold text-foreground">'</span>
                </div>
                <div className="flex flex-1 items-center rounded-lg border border-input bg-background focus-within:border-black">
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    min="0"
                    max="11"
                    value={inches}
                    onChange={e => {
                      const value = e.target.value;
                      if (
                        value === "" ||
                        (/^\d+$/.test(value) && Number(value) >= 0 && Number(value) < 12)
                      ) {
                        setInches(value);
                      }
                    }}
                    onKeyDown={e => {
                      if (e.key === "-" || e.key === "." || e.key === "e" || e.key === "E") {
                        e.preventDefault();
                      }
                    }}
                    className="w-full rounded-lg border-none bg-transparent py-3 pl-4 pr-1 text-foreground placeholder:text-muted-foreground focus:outline-none"
                    placeholder="0"
                  />
                  <span className="pr-3 text-base font-bold text-foreground">''</span>
                </div>
              </div>
            ) : (
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={cms}
                onChange={e => {
                  const value = e.target.value;
                  if (
                    value === "" ||
                    /^\d{1,4}$/.test(value)
                  ) {
                    setCms(value);
                  }
                }}
                onKeyDown={e => {
                  if (e.key === "-" || e.key === "." || e.key === "e" || e.key === "E") {
                    e.preventDefault();
                  }
                }}
                className="w-full rounded-lg border border-input bg-background px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-black focus:outline-none"
                placeholder="e.g. 170"
              />
            )}
            <p className="mt-1.5 text-xs text-muted-foreground">
              {unit === "Ft - Inch" ? "e.g. 5 feet 8 inches" : "e.g. 170 Cms"}
            </p>
          </div>

          {/* Weight Section */}
          <div className="mb-4">
            <Label className="mb-3 block text-base font-semibold text-foreground">
              Weight
            </Label>

            {/* Weight Unit Tabs */}
            <div className="mb-4 flex gap-2">
              <button
                className={cn(
                  "flex-1 rounded-lg px-4 py-2.5 text-sm font-semibold transition-all",
                  weightUnit === "Lbs"
                    ? "bg-[#737DFE] text-white shadow-sm"
                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                )}
                onClick={() => {
                  setWeightUnit("Lbs");
                  setWeight("");
                }}
              >
                Lbs
              </button>
              <button
                className={cn(
                  "flex-1 rounded-lg px-4 py-2.5 text-sm font-semibold transition-all",
                  weightUnit === "Kg"
                    ? "bg-[#737DFE] text-white shadow-sm"
                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                )}
                onClick={() => {
                  setWeightUnit("Kg");
                  setWeight("");
                }}
              >
                Kg
              </button>
            </div>

            {/* Weight Input */}
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={weight}
              onChange={e => {
                const value = e.target.value;
                if (value === "" || (/^\d{1,3}$/.test(value) && Number(value) >= 0)) {
                  setWeight(value);
                }
              }}
              onKeyDown={e => {
                if (e.key === "-" || e.key === "." || e.key === "e" || e.key === "E") {
                  e.preventDefault();
                }
              }}
              className="w-full rounded-lg border border-input bg-background px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-black focus:outline-none"
              placeholder={weightUnit === "Lbs" ? "e.g. 150" : "e.g. 68"}
            />

            {/* Helper Text */}
            <p className="mt-2 text-xs text-muted-foreground">
              {weightUnit === "Lbs"
                ? "Enter weight in pounds (e.g. 150 lbs)"
                : "Enter weight in kilograms (e.g. 68 kg)"}
            </p>
          </div>

          {/* Validation Error Messages */}
          {!canProceed &&
            (feet !== "" || inches !== "" || cms !== "" || weight !== "") && (
              <div className="mb-4 rounded-lg border border-orange-200 bg-orange-50 p-3 dark:border-orange-900/50 dark:bg-orange-900/20">
                <p className="mb-2 text-sm font-semibold text-orange-800 dark:text-orange-400">
                  ⚠️ Please check your entries
                </p>
                <div className="space-y-1 text-xs text-orange-700 dark:text-orange-300">
                  {/* Height validation messages */}
                  {!isHeightValid() &&
                    (feet !== "" || inches !== "" || cms !== "") && (
                      <div>
                        {unit === "Ft - Inch" ? (
                          <>
                            {feet !== "" &&
                              (Number(feet) <= 0 || Number(feet) > 10) && (
                                <p>• Height (feet) should be between 1-10</p>
                              )}
                            {inches !== "" &&
                              (Number(inches) < 0 || Number(inches) >= 12) && (
                                <p>• Height (inches) should be between 0-11</p>
                              )}
                            {(feet === "" || inches === "") && (
                              <p>• Please enter both feet and inches</p>
                            )}
                          </>
                        ) : (
                          <>
                            {cms !== "" && Number(cms) <= 0 && (
                              <p>• Please enter a valid height in Cms</p>
                            )}
                          </>
                        )}
                      </div>
                    )}

                  {/* Weight validation messages */}
                  {!isWeightValid() && weight !== "" && (
                    <div>
                      {Number(weight) < 3 && (
                        <p>
                          • Weight seems too low. Please enter at least 3{" "}
                          {weightUnit.toLowerCase()}
                        </p>
                      )}
                      {Number(weight) > (weightUnit === "Lbs" ? 1000 : 500) && (
                        <p>
                          • Weight exceeds maximum limit of{" "}
                          {weightUnit === "Lbs" ? "1000 lbs" : "500 kg"}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

          {/* Button Container */}
          <div className="mt-auto pt-4">
            <Button
              onClick={handleSave}
              disabled={!canProceed}
              className={gluceraAssessmentCta}
            >
              Next
            </Button>
          </div>
        </div>

        {/* Safe area padding */}
        <div className="pb-safe p-2" />
      </div>
    </div>
  );
};

export default HeightWeightPage;
